import { Footer } from "@/components/Footer";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { CheckCircle, CheckCircle2, ArrowRight, Code, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { requestAccessCsv } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { validateEmail } from "@/lib/emailValidation";
import zipyLogo from "@/assets/zipy-logo.png";

const Zipy = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    const validation = validateEmail(email);
    if (!validation.valid) {
      toast({ variant: "destructive", title: "Invalid email", description: validation.message, duration: 5000 });
      return;
    }
    setIsSubmitting(true);
    try {
      const response: any = await requestAccessCsv(email);
      if (response && response?.status && response?.status >= 400) throw new Error(response.statusText);
      setIsSubmitted(true);
      toast({ title: "🎉 You're on the list!", description: "We'll notify you when AI starts crushing your production bugs.", duration: 5000 });
    } catch (err) {
      console.error(err);
      toast({ variant: "destructive", title: "Error", description: "Please refresh the page and try again.", duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen">
      <ThemeToggle />
      <section className="py-8 px-4 border-b border-border">
        <div className="container mx-auto max-w-6xl">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">← Back to Home</Link>
        </div>
      </section>

      <section className="py-16 sm:py-24 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="text-center md:text-left">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-foreground">Fix Zipy Bugs with Healion</h1>
              <p className="text-xl text-muted-foreground mb-8">
                Connect Zipy's session replay and error monitoring to Healion for automatic bug detection, prioritization, and AI-powered fixes via webhook
              </p>
            </div>
            <div className="relative h-[300px] rounded-xl overflow-hidden bg-background border border-border flex items-center justify-center p-8">
              <img src={zipyLogo} alt="Zipy Logo" className="w-48 h-48 object-contain" />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold mb-12 text-center">How It Works</h2>
          <div className="space-y-8">
            {[
              { step: "1", title: "Zipy Captures Errors", desc: "Zipy monitors your web application, capturing errors, session replays, and user interactions in real-time across all environments." },
              { step: "2", title: "Webhook Triggers Healion", desc: "When Zipy detects an error, it sends a webhook to Healion with complete error context, session replay data, and stack traces." },
              { step: "3", title: "AI Prioritizes & Analyzes", desc: "Healion's AI analyzes error severity, user impact, and frequency to prioritize fixes based on business criticality." },
              { step: "4", title: "Root Cause Analysis", desc: "Using Zipy's session replay data and error context, the AI identifies the exact root cause and generates a targeted fix." },
              { step: "5", title: "PR Created & Ready", desc: "A pull request is automatically created with the fix, including links to the Zipy session replay for full context." },
            ].map((item) => (
              <Card key={item.step} className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <span className="font-bold text-accent">{item.step}</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold mb-2">{item.title}</h3>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold mb-12 text-center">Setup Integration</h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Code className="w-5 h-5 text-accent" />Step 1: Configure Zipy Webhook
              </h3>
              <Card className="p-6 bg-muted/50">
                <ol className="space-y-3 list-decimal list-inside text-muted-foreground">
                  <li>Log in to your Zipy dashboard</li>
                  <li>Navigate to Settings → Integrations → Webhooks</li>
                  <li>Click "Add Webhook"</li>
                  <li>Enter the Healion webhook URL:</li>
                </ol>
                <div className="mt-4 p-4 bg-background rounded-md font-mono text-sm border border-border">
                  https://api.healion.ai/webhooks/zipy
                </div>
                <p className="mt-4 text-sm text-muted-foreground">
                  Select event types: <span className="font-semibold">Error Events, Crash Reports</span>
                </p>
              </Card>
            </div>
            <div>
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-accent" />Step 2: Configure Alert Rules
              </h3>
              <Card className="p-6 bg-muted/50">
                <ol className="space-y-3 list-decimal list-inside text-muted-foreground">
                  <li>Go to Alerts → Create New Alert</li>
                  <li>Define error conditions and thresholds</li>
                  <li>Select "Webhook" as the notification channel</li>
                  <li>Choose the Healion webhook you configured</li>
                </ol>
              </Card>
            </div>
            <div>
              <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-accent" />Step 3: Test the Integration
              </h3>
              <Card className="p-6 bg-muted/50">
                <p className="text-muted-foreground mb-4">Trigger a test error and verify:</p>
                <ul className="space-y-2 list-disc list-inside text-muted-foreground">
                  <li>Zipy captures the error with session replay</li>
                  <li>Healion receives the webhook notification</li>
                  <li>A pull request is created with the fix</li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-muted/30">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-3xl font-bold mb-12 text-center">Key Benefits</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              { title: "Session Replay Context", desc: "AI uses Zipy's session replays to understand exactly how users triggered the bug." },
              { title: "Real-time Detection", desc: "Errors are caught and fixes generated within minutes of occurrence." },
              { title: "Smart Prioritization", desc: "Bugs are ranked by user impact, frequency, and business criticality." },
              { title: "Zero Configuration", desc: "Works with your existing Zipy setup — just add the webhook URL." },
            ].map((b, i) => (
              <Card key={i} className="p-6">
                <CheckCircle className="w-8 h-8 text-accent mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">{b.title}</h3>
                <p className="text-muted-foreground">{b.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="container mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-3xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-muted-foreground mb-8">Connect Zipy to Healion and start fixing bugs automatically</p>
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-3">
              <Input type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 text-base bg-background border-border focus:border-foreground" disabled={isSubmitting} />
              <Button type="submit" size="lg" className="w-full gap-2 hover:scale-105 transition-transform" disabled={isSubmitting}>
                {isSubmitting ? "Joining..." : "Join Waitlist"}<ArrowRight className="w-4 h-4" />
              </Button>
            </form>
          ) : (
            <div className="max-w-md mx-auto flex items-center gap-3 px-4 py-3 rounded-lg bg-accent/10 border border-accent/30">
              <CheckCircle2 className="w-6 h-6 text-accent flex-shrink-0" />
              <div className="text-left">
                <p className="font-medium text-foreground text-sm">You're in! 🚀</p>
                <p className="text-xs text-muted-foreground">We'll reach out when it's your turn.</p>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default Zipy;
